import type { RowSelectionState } from '../tanstack-table'

/**
 * The row IDs from the anchor to the target, inclusive, in display order. Without a loaded
 * anchor, the range is the target alone.
 */
export const getRowRangeIds = (
  rowIds: readonly string[],
  anchorId: null | string,
  targetId: string,
): string[] => {
  const targetIndex = rowIds.indexOf(targetId)
  if (targetIndex === -1) return []

  const anchorIndex = anchorId === null ? -1 : rowIds.indexOf(anchorId)
  if (anchorIndex === -1) return [targetId]

  return rowIds.slice(Math.min(anchorIndex, targetIndex), Math.max(anchorIndex, targetIndex) + 1)
}

/**
 * Applies one value to a range on top of a base selection. Base IDs that are no longer loaded
 * are dropped, so a drag's snapshot cannot bring back rows that left the table mid-drag.
 */
export const applyRowRange = (
  base: RowSelectionState,
  rangeIds: readonly string[],
  value: boolean,
  loadedRowIds: ReadonlySet<string>,
): RowSelectionState => {
  const next: RowSelectionState = {}
  for (const id of Object.keys(base)) {
    if (loadedRowIds.has(id)) next[id] = true
  }
  for (const id of rangeIds) {
    if (value) next[id] = true
    else delete next[id]
  }

  return next
}

/** How far inside the rows' top and bottom edges a drag starts scrolling, in pixels. */
const AUTO_SCROLL_EDGE = 40
/** The fastest a drag scrolls, in pixels per frame. */
const AUTO_SCROLL_MAX_STEP = 24

/**
 * The pixels to scroll per frame while a drag holds the pointer near or past the rows' top or
 * bottom edge: negative scrolls up, 0 between the edge bands.
 */
export const getAutoScrollStep = (clientY: number, bounds: { bottom: number; top: number }) => {
  const intoTop = bounds.top + AUTO_SCROLL_EDGE - clientY
  if (intoTop > 0) {
    return -Math.min(
      AUTO_SCROLL_MAX_STEP,
      Math.ceil((intoTop / AUTO_SCROLL_EDGE) * AUTO_SCROLL_MAX_STEP),
    )
  }

  const intoBottom = clientY - (bounds.bottom - AUTO_SCROLL_EDGE)
  if (intoBottom > 0) {
    return Math.min(
      AUTO_SCROLL_MAX_STEP,
      Math.ceil((intoBottom / AUTO_SCROLL_EDGE) * AUTO_SCROLL_MAX_STEP),
    )
  }

  return 0
}
